import { create } from 'zustand'
import useAppStore from '../../../store/useAppStore'
import { profileApi } from '../services/profileApi'

const syncAppAvatar = (ava) => {
  const appStore = useAppStore.getState()
  if (appStore.user) appStore.setUser({ ...appStore.user, ava, avatar: ava })
}

// Защита от гонки при быстрой смене slug / повторных загрузках
let loadRequestId = 0

export const useProfileStore = create((set, get) => ({
  profile: null,
  isLoading: false,
  // 'not_found' (HTTP 404) | 'load_error' (HTTP 500 / сеть) | null
  error: null,
  isLikePending: false,
  isSavingSettings: false,
  isUploadingBackground: false,
  isUpdatingAvatar: false,

  /**
   * Загрузка профиля. slug === null/undefined — свой профиль.
   * Кеш на бэкенде 60 сек, на фронте не кешируем — каждый заход = новый запрос.
   */
  loadProfile: async (slug) => {
    const requestId = ++loadRequestId
    const revision = useAppStore.getState().languageRevision
    set({ isLoading: true, error: null, profile: null })

    const { ok, status, data } = slug
      ? await profileApi.getProfile(slug)
      : await profileApi.getMyProfile()

    if (requestId !== loadRequestId || revision !== useAppStore.getState().languageRevision) return

    if (status === 404) {
      set({ error: 'not_found', isLoading: false })
      return
    }
    if (status === 429) {
      set({ error: 'rate_limited', isLoading: false })
      return
    }

    if (!ok || !data?.profile) {
      set({ error: 'load_error', isLoading: false })
      return
    }

    set({ profile: data.profile, isLoading: false })
  },

  /**
   * Лайк: отправляем желаемое состояние (1/0), счётчик обновляем из ответа.
   * @returns {Promise<{ok: boolean, status?: number, error?: string}|null>}
   */
  setLike: async (slug, like) => {
    if (get().isLikePending) return null
    set({ isLikePending: true })

    const { ok, status, data } = await profileApi.setLike(slug, like)
    set({ isLikePending: false })

    if (ok && typeof data?.likes === 'number') {
      set({ profile: { ...get().profile, likes: data.likes, liked_by_me: data.liked_by_me } })
      return { ok: true }
    }

    return { ok: false, status, error: data?.error }
  },

  /** Смена уровня доступа. Ответ: { result: true, visibility } — перезапрашивать профиль не обязательно */
  setVisibility: async (visibility) => {
    const { ok, status, data } = await profileApi.setVisibility(visibility)
    if (ok && data?.result) {
      set({ profile: { ...get().profile, visibility: data.visibility } })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Сохранение цветов (только изменённые ключи собирает вызывающий код).
   * Ответ 200 — свой профиль целиком, сразу перерисовываем экран им.
   */
  saveColors: async (colors) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await profileApi.saveColors(colors)
    set({ isSavingSettings: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Переключатель «Показывать мой Telegram»: отправляем желаемое состояние 1/0.
   * Ответ 200 — свой профиль целиком (кеш бэкенда сброшен), сразу перерисовываем экран им.
   * @returns {Promise<{ok: boolean, status?: number, error?: string}|null>}
   */
  setTelegramShow: async (show) => {
    const { ok, status, data } = await profileApi.setContacts(show ? 1 : 0)
    if (ok && data?.profile) {
      set({ profile: data.profile })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Загрузка картинки-фона карточки профиля. settings — текущие настройки
   * отображения (fit/position/overlay/blur), применяются к загруженной картинке.
   * Ответ 200 — свой профиль целиком; возвращаем background для синхронизации
   * локального состояния редактора.
   * @returns {Promise<{ok: boolean, status?: number, error?: string, background?: object|null}|null>}
   */
  uploadBackground: async (file, settings) => {
    if (get().isUploadingBackground) return null
    set({ isUploadingBackground: true })

    const { ok, status, data } = await profileApi.uploadBackground(file, settings)
    set({ isUploadingBackground: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      return { ok: true, background: data.profile.background ?? null }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Частичное сохранение настроек фона (без перезагрузки картинки) — для
   * автосохранения слайдеров с debounce. Ответ 200 — свой профиль целиком.
   * Пока картинки нет, настройки в ответе не видны — вызывающий код хранит
   * отправленное состояние локально.
   * @returns {Promise<{ok: boolean, status?: number, error?: string}|null>}
   */
  saveBackgroundSettings: async (settings) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await profileApi.saveBackgroundSettings(settings)
    set({ isSavingSettings: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Снятие фона: ответ 200 — свой профиль с background: null, настройки
   * отображения бэкенд сбрасывает к дефолтам.
   * @returns {Promise<{ok: boolean, status?: number, error?: string}|null>}
   */
  deleteBackground: async () => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await profileApi.deleteBackground()
    set({ isSavingSettings: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  uploadAvatar: async (file) => {
    if (get().isUpdatingAvatar) return null
    set({ isUpdatingAvatar: true })
    const { ok, status, data } = await profileApi.uploadAvatar(file)
    set({ isUpdatingAvatar: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      syncAppAvatar(data.profile.ava ?? null)
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  deleteAvatar: async () => {
    if (get().isUpdatingAvatar) return null
    set({ isUpdatingAvatar: true })
    const { ok, status, data } = await profileApi.deleteAvatar()
    set({ isUpdatingAvatar: false })

    if (ok && data?.profile) {
      set({ profile: data.profile })
      syncAppAvatar(data.profile.ava ?? null)
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Prepared message «Поделиться»; id не кешируем — новый на каждое нажатие */
  shareProfile: async () => {
    const { ok, status, data } = await profileApi.share()
    return { ok, status, data, error: data?.error }
  },

  invalidateLocalizedData: () => {
    loadRequestId += 1
    set({ profile: null, isLoading: false, error: null })
  },
  reset: () => {
    loadRequestId += 1
    set({
      profile: null,
      error: null,
      isLoading: false,
      isLikePending: false,
      isSavingSettings: false,
      isUploadingBackground: false,
      isUpdatingAvatar: false
    })
  }
}))
